import { IconX } from '@tabler/icons-react';
import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';

import { DataSheet } from '@components/ui/DataSheet';
import type { DataSheetColumn } from '@components/ui/DataSheet';

interface TransferLinesModalProps<T extends Record<string, unknown>> {
    /** A wider dialog for tables with many columns (the channel shipment timeline). */
    wide?: boolean;
    isOpen: boolean;
    onClose: () => void;
    /** Leading glyph in the title, if any. */
    icon?: React.ReactNode;
    title: string;
    /** The SKU, the scope and the total — what the rows below add up to. */
    subtitle: React.ReactNode;
    loading: boolean;
    /** Null until the first load (or after a failed one); empty when nothing is open. */
    lines: T[] | null;
    columns: DataSheetColumn<T>[];
    getRowKey: (line: T) => string;
    emptyText: string;
    /** One line under the table saying what the rows are and whether they count in stock. */
    footerNote: React.ReactNode;
    footerLink?: { to: string; label: string };
}

/**
 * The drill-in behind a transfer-shaped cell on the inventory grids: one row per open STN
 * line (a gate pass between two of the brand's warehouses, a shipment into a channel's FC),
 * summing to the number that was clicked. The chrome is shared so every such cell opens the
 * same thing; what differs — the fetch, the columns, the wording — is the caller's.
 */
function TransferLinesModal<T extends Record<string, unknown>>({
    isOpen,
    onClose,
    icon,
    title,
    subtitle,
    loading,
    lines,
    columns,
    getRowKey,
    emptyText,
    footerNote,
    footerLink,
    wide = false,
}: TransferLinesModalProps<T>): React.ReactElement | null {
    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className={`w-full ${wide ? 'max-w-7xl' : 'max-w-4xl'} overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200`}>
                <div className="flex items-start justify-between border-b border-slate-100 p-6 pb-4">
                    <div>
                        <h3 className="flex items-center gap-2 text-lg font-bold text-slate-900">
                            {icon}
                            {title}
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">
                            {subtitle}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="cursor-pointer rounded-full p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
                    >
                        <IconX className="h-5 w-5" />
                    </button>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-6">
                    {loading ? (
                        <div className="py-8 text-center text-sm text-slate-400">
                            Loading...
                        </div>
                    ) : !lines || lines.length === 0 ? (
                        <div className="py-8 text-center text-sm text-slate-400">
                            {emptyText}
                        </div>
                    ) : (
                        <>
                            <DataSheet
                                data={lines}
                                columns={columns}
                                getRowKey={getRowKey}
                                bare
                                hideHeaderBar
                            />
                            <div className="mt-3 flex items-start justify-between gap-4 text-xs text-slate-500">
                                <span>{footerNote}</span>
                                {footerLink && (
                                    <Link
                                        to={footerLink.to}
                                        className="font-semibold whitespace-nowrap text-blue-600 hover:underline"
                                    >
                                        {footerLink.label}
                                    </Link>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

export default TransferLinesModal;
